/**
 * Barracks (TOWERS_V2): no shot of its own; it keeps `soldiers` armoured soldiers posted on the road cell nearest the
 * tower within its range. They are troop entities (entities.Troop with `ownerTowerId` = the tower) kept in
 * `TroopSystem.soldiers`, so the fortress troop rules hold for them: they walk there over walkable cells, settle on a
 * formation slot across the road, block ground enemies that walk into them (TroopSystem.blocks — a blow is cut by their
 * armour) and strike the nearest ground enemy within `soldierReach`. A fallen soldier musters again at the tower
 * `soldierRespawnTicks` later. Strength: health = soldierHp × tier damageMult × tower level multiplier; damage = the
 * tower's damage (`LevelSim.damageOf`: tier, level, Beacon aura, gear); armour = the tier's `soldierArmour`; the strike
 * cadence is the tower's fire interval (gear fire rate shortens it). Kills and damage are credited to the tower.
 */
import { DamageType, TowerArchetype, isBarracks } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { Fnv1a64 } from "../maths/fnv1a64";
import { Cell } from "../world/cell";
import { distanceSquared } from "../world/roadGeometry";
import { Enemy, Tower, Troop } from "./entities";
import { SimEventKind } from "./events";
import type { LevelSim } from "./levelSim";
import { mythicValue } from "./mythics";
import { applyDamage } from "./systems/combat";
import { BASE_TIERS, branchOf, skill, tierLine } from "./branches/ascension";
import { stun } from "./branches/statuses";

/** Marks the Barracks block in the state hash (written only while a Barracks stands). */
const BARRACKS_HASH_TAG = 0xba77;
/** A post's soldiers stand in one rank across the road. */
const POST_ROWS = 1;

/** One Barracks' squad: where it holds and its soldiers (fixed slots, as the fortress formation). */
export interface Squad {
  readonly towerId: number;
  /** The road cell it holds; null while no road cell lies in range (looked for again every tick). */
  post: Cell | null;
  readonly members: Troop[];
}

/** A Barracks was placed: its squad musters at the tower at once and walks to its post. */
export function raiseSquad(sim: LevelSim, t: Tower): void {
  const a = sim.balance.towers[t.archetype];
  if (!isBarracks(a)) return;
  const count = a.soldiers + mythicValue(sim, t.archetype, sim.mythics.barracksFourth).floorToInt();
  const squad: Squad = { towerId: t.id, post: postFor(sim, t), members: [] };
  for (let i = 0; i < count; i++) {
    const s = new Troop();
    s.ownerTowerId = t.id;
    squad.members.push(s);
    muster(sim, squad, t, s);
  }
  sim.squads.push(squad);
  rebuildSoldiers(sim);
}

/** A Barracks was sold: its soldiers leave the field (TroopDied with no killer). */
export function disbandSquad(sim: LevelSim, towerId: number): void {
  const i = sim.squads.findIndex((q) => q.towerId === towerId);
  if (i < 0) return;
  for (const s of sim.squads[i].members) if (s.alive) { s.alive = false; sim.events.add(SimEventKind.TroopDied, s.id, 0); }
  sim.squads.splice(i, 1);
  rebuildSoldiers(sim);
}

/** A Barracks went up a tier: its living soldiers keep their share of health at the new maximum and wear the new armour. */
export function promoteSquad(sim: LevelSim, t: Tower): void {
  const q = sim.squads.find((x) => x.towerId === t.id);
  if (q === undefined) return;
  const a = sim.balance.towers[t.archetype];
  const maxHp = soldierMaxHp(sim, t, a);
  for (const s of q.members) {
    s.armour = tierLine(sim, t.archetype, t.tier).soldierArmour;
    if (!s.alive) continue;
    s.hp = s.hp.mul(maxHp).div(Fix64.max(s.maxHp, Fix64.One));
    s.maxHp = maxHp;
  }
}

/** Once per wave tick, after the fortress troops: respawn timers, walking to the post, settling and striking. */
export function tickSquads(sim: LevelSim): void {
  for (const q of sim.squads) {
    const t = sim.findTower(q.towerId);
    if (t === null) continue;
    const a = sim.balance.towers[t.archetype];
    if (q.post === null) {
      q.post = postFor(sim, t);
      if (q.post !== null) for (const s of q.members) if (s.alive) s.path = sim.troops.pathFrom(new Cell(s.cx, s.cy), q.post);
    }
    const facing = q.post !== null ? sim.troops.facingAt(q.post) : sim.grid.baseHeading;
    // Rangers' Camp Volley (tier 4): when its clock has run, every ranger looses at once this tick.
    const volley = t.tier > BASE_TIERS && t.skillTimer === 0 && t.disabledTicksLeft === 0 && skill(sim, t, "rangerVolley") !== null;
    for (let i = 0; i < q.members.length; i++) {
      const s = q.members[i];
      if (!s.alive) {
        // a disabled Barracks (Bomber) musters no one until it is working again
        if (t.disabledTicksLeft > 0) continue;
        if (--s.respawnLeft <= 0) muster(sim, q, t, s);
        continue;
      }
      if (s.path.length > 0) sim.troops.walk(s);
      else if (q.post !== null) sim.troops.settle(s, sim.troops.formationPoint(q.post, i, q.members.length, POST_ROWS, facing));
      if (t.tier > BASE_TIERS) { soldierTier4(sim, t, s, volley); continue; }
      if (s.attackCooldown > 0) { s.attackCooldown--; continue; }
      const target = nearestGroundEnemy(sim, s, a.soldierReach);
      if (target === null) continue;
      s.attackCooldown = sim.fireIntervalOf(t);
      sim.events.add(SimEventKind.TroopAttacked, s.id, target.id);
      applyDamage(sim, target, t, sim.damageOf(t), DamageType.Physical);
    }
    if (volley) t.skillTimer = skill(sim, t, "rangerVolley")!.params.int("cooldownTicks");
  }
}

/**
 * A tier-4 soldier's turn (sim/branches): Knights' Hall — Shield Wall holds the road wider, Holy Strike hits around the
 * target; Rangers' Camp — bows from the post (flyers too), Ambush stuns an enemy on its first arrow, Volley (when its
 * clock has run: `volley`) looses at several targets at once.
 */
function soldierTier4(sim: LevelSim, t: Tower, s: Troop, volley: boolean): void {
  const b = branchOf(sim, t)!;
  const a = sim.balance.towers[t.archetype];
  const wall = skill(sim, t, "shieldWall");
  s.blockBonus = wall !== null ? wall.params.num("blockBonus") : Fix64.Zero;
  const reach = b.tier4.num("soldierReach", a.soldierReach);
  const air = b.tier4.int("hitsAir") > 0;
  const damage = sim.damageOf(t);
  if (volley) {
    const v = skill(sim, t, "rangerVolley")!;
    const targets = nearestEnemies(sim, s, reach, air, v.params.int("targets"));
    if (targets.length > 0) sim.events.add(SimEventKind.SkillFired, t.id, v.index, targets[0].id);
    for (const e of targets) strike(sim, t, s, e, damage);
  }
  if (s.attackCooldown > 0) { s.attackCooldown--; return; }
  const target = nearestEnemies(sim, s, reach, air, 1)[0] ?? null;
  if (target === null) return;
  s.attackCooldown = sim.fireIntervalOf(t);
  strike(sim, t, s, target, damage);
  const holy = skill(sim, t, "holyStrike");
  if (holy === null) return;
  const r = holy.params.num("radius"), r2 = r.mul(r), splash = damage.mul(holy.params.num("damageMult"));
  sim.areaDepth++;
  for (const e of sim.enemySlots) {
    if (!e.alive || e === target || e.isAir || e.burrowed || distanceSquared(target.x, target.y, e.x, e.y).gt(r2)) continue;
    applyDamage(sim, e, t, splash, DamageType.Physical);
  }
  sim.areaDepth--;
}

/** One blow or arrow: TroopAttacked, the damage, and Ambush's stun on an enemy it has not yet ambushed. */
function strike(sim: LevelSim, t: Tower, s: Troop, e: Enemy, damage: Fix64): void {
  if (!e.alive) return;
  sim.events.add(SimEventKind.TroopAttacked, s.id, e.id);
  const ambush = skill(sim, t, "ambush");
  if (ambush !== null && !e.ambushed) {
    e.ambushed = true;
    stun(sim, e, t, ambush.params.int("stunTicks"));
    sim.events.add(SimEventKind.SkillFired, t.id, ambush.index, e.id);
  }
  applyDamage(sim, e, t, damage, DamageType.Physical);
}

/** Up to `count` enemies nearest the soldier within `reach` (ground, and air when `air`), nearest first, ties → lower id. */
function nearestEnemies(sim: LevelSim, s: Troop, reach: Fix64, air: boolean, count: number): Enemy[] {
  const r2 = reach.mul(reach);
  const out: Enemy[] = [];
  for (const e of sim.enemySlots) {
    if (!e.alive || e.burrowed || (e.isAir && (!air || !sim.targetable(e)))) continue;   // a Burrower passes under the post
    if (distanceSquared(s.x, s.y, e.x, e.y).le(r2)) out.push(e);
  }
  out.sort((p, q) => distanceSquared(s.x, s.y, p.x, p.y).cmp(distanceSquared(s.x, s.y, q.x, q.y)) || p.id - q.id);
  return out.slice(0, count);
}

/** Towers placed or sold change what is walkable: every soldier on its way re-routes to its post. */
/**
 * A Barracks was moved (M55): its squad takes the post nearest the new square, the living soldiers march there from
 * where they stand, and the fallen muster at the new square when their time is up.
 */
export function relocateSquad(sim: LevelSim, t: Tower): void {
  const q = sim.squads.find((x) => x.towerId === t.id);
  if (q === undefined) return;
  q.post = postFor(sim, t);
  for (const s of q.members) if (s.alive) s.path = q.post === null ? [] : sim.troops.pathFrom(new Cell(s.cx, s.cy), q.post);
}

export function rerouteSquads(sim: LevelSim): void {
  for (const q of sim.squads) {
    if (q.post === null) continue;
    for (const s of q.members) if (s.alive && s.path.length > 0) s.path = sim.troops.pathFrom(new Cell(s.cx, s.cy), q.post);
  }
}

/** Ticks before a fallen soldier musters again (TroopSystem calls this when a blow fells it). */
export function soldierRespawnTicks(sim: LevelSim, s: Troop): number {
  const t = sim.findTower(s.ownerTowerId);
  return t === null ? 0 : sim.balance.towers[t.archetype].soldierRespawnTicks;
}

export function writeSquadsHash(sim: LevelSim, h: Fnv1a64): void {
  if (sim.squads.length === 0) return;
  h.writeInt(BARRACKS_HASH_TAG);
  for (const q of sim.squads) {
    h.writeInt(q.towerId); h.writeInt(q.post?.x ?? -1); h.writeInt(q.post?.y ?? -1);
    for (const s of q.members) {
      h.writeInt(s.alive ? s.id : -s.respawnLeft);
      if (!s.alive) continue;
      h.writeRaw(s.hp.raw); h.writeRaw(s.x.raw); h.writeRaw(s.y.raw); h.writeInt(s.path.length); h.writeInt(s.attackCooldown);
    }
  }
}

/** The road cell nearest the tower within its range (ties → the one nearer the portal, which meets raiders first). */
export function postFor(sim: LevelSim, t: Tower): Cell | null {
  const r = sim.rangeOf(t);
  const r2 = r.mul(r);
  let best: Cell | null = null, bestD2 = Fix64.Zero;
  for (const c of sim.road.roadCells) {
    const d2 = Fix64.fromInt((c.x - t.cell.x) * (c.x - t.cell.x) + (c.y - t.cell.y) * (c.y - t.cell.y));
    if (d2.gt(r2) || !sim.troops.walkable(c)) continue;
    if (best === null || d2.lt(bestD2)) { best = c; bestD2 = d2; }
  }
  return best;
}

function soldierMaxHp(sim: LevelSim, t: Tower, a: TowerArchetype): Fix64 {
  // Tier 4: the branch's own health line (Knights 4.5×, Rangers 2.5×) rather than its damage line.
  const line = t.tier > BASE_TIERS ? branchOf(sim, t)?.tier4.num("soldierHpMult", tierLine(sim, t.archetype, t.tier).damageMult) ?? a.tiers[a.tiers.length - 1].damageMult : a.tiers[t.tier - 1].damageMult;
  return a.soldierHp.mul(line).mul(sim.balance.tables.levelMultAt(sim.towerLevels[t.archetype]));
}

/** A soldier (re)joins at full health at its tower's cell and sets off for the post. */
function muster(sim: LevelSim, q: Squad, t: Tower, s: Troop): void {
  const a = sim.balance.towers[t.archetype];
  s.id = sim.troops.allocateId();
  s.alive = true;
  s.maxHp = soldierMaxHp(sim, t, a);
  s.hp = s.maxHp;
  s.damage = Fix64.Zero;                                   // read live from the tower (damageOf) when it strikes
  s.armour = tierLine(sim, t.archetype, t.tier).soldierArmour;
  s.cx = t.cell.x; s.cy = t.cell.y;
  s.x = Fix64.fromInt(s.cx); s.y = Fix64.fromInt(s.cy);
  s.attackCooldown = 0;
  s.respawnLeft = 0;
  s.path = q.post === null ? [] : sim.troops.pathFrom(t.cell, q.post);
  sim.events.add(SimEventKind.TroopSpawned, s.id, s.cx, s.cy);
}

function rebuildSoldiers(sim: LevelSim): void {
  const list = sim.troops.soldiers;
  list.length = 0;
  for (const q of sim.squads) for (const s of q.members) list.push(s);
}

/** The ground enemy nearest this soldier within `reach` (ties → the lower id). */
function nearestGroundEnemy(sim: LevelSim, s: Troop, reach: Fix64): Enemy | null {
  const r2 = reach.mul(reach);
  let best: Enemy | null = null, bestD2 = Fix64.Zero;
  for (const e of sim.enemySlots) {
    if (!e.alive || e.isAir || e.burrowed) continue;
    const d2 = distanceSquared(s.x, s.y, e.x, e.y);
    if (d2.gt(r2)) continue;
    if (best === null || d2.lt(bestD2) || (d2.eq(bestD2) && e.id < best.id)) { best = e; bestD2 = d2; }
  }
  return best;
}
